import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getAllCounterparties, createCounterparty, updateCounterparty, deleteCounterparty } from '@/services/marketingService';
import * as Sentry from '@sentry/react';

const CounterpartiesPage = () => {
    const [counterparties, setCounterparties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCounterparty, setSelectedCounterparty] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState('all');
    const [formData, setFormData] = useState({
        companyName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        notes: '',
        isActive: true
    });

    const { apiFetch } = useAuth();

    useEffect(() => {
        fetchCounterparties();
    }, []);

    const fetchCounterparties = async () => {
        setIsLoading(true);
        try {
            const data = await getAllCounterparties(apiFetch);
            setCounterparties(data);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to load counterparties');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (counterparty = null) => {
        if (counterparty) {
            setSelectedCounterparty(counterparty);
            setFormData({
                companyName: counterparty.companyName || '',
                contactName: counterparty.contactName || '',
                contactEmail: counterparty.contactEmail || '',
                contactPhone: counterparty.contactPhone || '',
                notes: counterparty.notes || '',
                isActive: counterparty.isActive ?? true
            });
        } else {
            setSelectedCounterparty(null);
            setFormData({
                companyName: '',
                contactName: '',
                contactEmail: '',
                contactPhone: '',
                notes: '',
                isActive: true
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCounterparty(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.companyName.trim()) {
            toast.error('Company name is required');
            return;
        }

        setIsLoading(true);
        try {
            if (selectedCounterparty) {
                await updateCounterparty(apiFetch, selectedCounterparty.counterpartyId, formData);
                toast.success('Counterparty updated successfully');
            } else {
                await createCounterparty(apiFetch, formData);
                toast.success('Counterparty created successfully');
            }
            handleCloseModal();
            fetchCounterparties();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to save counterparty');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (counterparty) => {
        setSelectedCounterparty(counterparty);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!selectedCounterparty) return;

        setIsLoading(true);
        try {
            await deleteCounterparty(apiFetch, selectedCounterparty.counterpartyId);
            toast.success('Counterparty deleted successfully');
            setShowDeleteModal(false);
            setSelectedCounterparty(null);
            fetchCounterparties();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to delete counterparty');
        } finally {
            setIsLoading(false);
        }
    };

    const filteredCounterparties = counterparties.filter(cp => {
        const matchesSearch = cp.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            cp.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            cp.contactEmail?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesFilter = filterActive === 'all' || 
                            (filterActive === 'active' && cp.isActive) ||
                            (filterActive === 'inactive' && !cp.isActive);

        return matchesSearch && matchesFilter;
    });

    return (
        <div className="csi-container">
            <h1>Marketing Counterparties</h1>

            <div className="toolbar">
                <div className="search-filter-group">
                    <input
                        type="text"
                        placeholder="Search counterparties..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control search-input"
                    />
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="form-control filter-select"
                    >
                        <option value="all">All</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                    </select>
                </div>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    Add Counterparty
                </button>
            </div>

            {isLoading ? (
                <div className="loading">Loading counterparties...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Company Name</th>
                                <th>Contact Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCounterparties.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center' }}>
                                        No counterparties found
                                    </td>
                                </tr>
                            ) : (
                                filteredCounterparties.map(cp => (
                                    <tr key={cp.counterpartyId}>
                                        <td>{cp.companyName}</td>
                                        <td>{cp.contactName || '-'}</td>
                                        <td>{cp.contactEmail || '-'}</td>
                                        <td>{cp.contactPhone || '-'}</td>
                                        <td>
                                            <span className={`badge ${cp.isActive ? 'badge-success' : 'badge-secondary'}`}>
                                                {cp.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleOpenModal(cp)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDeleteClick(cp)}
                                                style={{ marginLeft: '5px' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>{selectedCounterparty ? 'Edit Counterparty' : 'Add Counterparty'}</h3>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="companyName">Company Name *</label>
                                    <input
                                        type="text"
                                        id="companyName"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="contactName">Contact Name</label>
                                    <input
                                        type="text"
                                        id="contactName"
                                        name="contactName"
                                        value={formData.contactName}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="contactEmail">Contact Email</label>
                                    <input
                                        type="email"
                                        id="contactEmail"
                                        name="contactEmail"
                                        value={formData.contactEmail}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="contactPhone">Contact Phone</label>
                                    <input
                                        type="tel"
                                        id="contactPhone"
                                        name="contactPhone"
                                        value={formData.contactPhone}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="notes">Notes</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        rows="3"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                        />
                                        {' '}Active
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseModal}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete {selectedCounterparty?.companyName}?</p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger" 
                                onClick={handleDelete}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default CounterpartiesPage;
